import React, { useCallback, useContext, useState } from 'react'
import moment from 'moment'
import { SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'LuxKit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'LuxKit/Modal/Modal'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import { INTL_DATE_TIME_FORMAT, INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import styled from 'styled-components'
import BookingProtectionViaInfoModal from 'checkout/Components/CheckoutSteps/Review/BookingProtection/BookingProtectionViaInfoModal'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { BOOKING_PROTECTION_ELIGABILITY_DAY_RANGE } from 'constants/bookingProtection'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  propertyTimezoneOffset?: number;
  checkInDate?: string | moment.Moment;
  isFlashUSP?: boolean;
  isBookingProtectionEnabled?: boolean;
  cartIncludesFlights?: boolean;
}

function OfferPropertyCancellationPolicies(props: Props) {
  const { propertyTimezoneOffset, checkInDate, isFlashUSP, isBookingProtectionEnabled = false, cartIncludesFlights } = props
  const [modalOpen, setModalOpen] = useState(false)
  const intlDateFormatter = useIntlDateFormatter()

  const startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  const startBasedOnCheckIn = checkInDate && (propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days') : moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days'))
  let startDate: moment.Moment | undefined
  if (startBasedOnCheckIn) {
    startDate = moment.min(startBasedOnToday, startBasedOnCheckIn)
  }

  const bookingProtectionLastEligibleRefundDate = checkInDate ? moment(checkInDate).add(BOOKING_PROTECTION_ELIGABILITY_DAY_RANGE, 'days') : undefined

  const showModal = useContext(ModalContext)

  const onItemClick = useCallback(() => {
    if (isBookingProtectionEnabled && startDate && !cartIncludesFlights) {
      showModal(<BookingProtectionViaInfoModal cancellationType="free" cancelUntilFullRefundDate={startDate} bookingProtectionLastEligibleRefundDate={bookingProtectionLastEligibleRefundDate} />)
    } else {
      setModalOpen(true)
    }
  }, [isBookingProtectionEnabled, showModal, startDate, bookingProtectionLastEligibleRefundDate, cartIncludesFlights])

  const onClose = useCallback(() => {
    setModalOpen(false)
  }, [])

  if (startDate?.isBefore(moment())) {
    return null
  }

  return <>
    {!isFlashUSP && <Clickable onClick={onItemClick}>
      <BodyText variant="medium" weight="bold" as="span" colour="highlight-secondary">Free cancellation</BodyText>{' '}
      {!startDate && <BodyText variant="medium" as="span">within 7 days of purchase (Hotel only)</BodyText>}
      {startDate && <BodyText variant="medium" as="span">until {intlDateFormatter(startDate, INTL_DAY_MONTH_NAME_SHORT)} (Hotel only)</BodyText>}
      {' '}<MiddledTooltipIcon size="XS" />
    </Clickable>}
    {isFlashUSP && <TextLink
      size="medium"
      colour="highlight-secondary"
      underline={false}
      data-type="7day"
      onClick={onItemClick}
      startIcon={<LineRefundIcon />}
    >
      Free cancellation
    </TextLink>}
    <Modal
      title="Free cancellation"
      isOpen={modalOpen}
      onClose={onClose}
    >
      {!checkInDate && <BodyText variant="medium" as="p">
        Things don’t always work out. Our 7-day ‘change of mind’ guarantee is there to help.{' '}
        Limited Time Lux Exclusive bookings (excluding flights) may be cancelled with a full refund{' '}
        provided that cancellation occurs strictly within 7 days from the date of purchase{' '}
        and provided that the cancellation is made no less than 14 days prior to the check-in date.{' '}
        Please note: flights and experiences are subject to the refund policies of the respective operator.
      </BodyText>}
      {checkInDate && startDate && <BodyText variant="medium" as="p">
        Standard bookings can be cancelled with a full refund provided the cancellation is made by {intlDateFormatter(startDate, INTL_DATE_TIME_FORMAT)} (local time of property).
      </BodyText>}
      <HelpPane />
    </Modal>
  </>
}

export default OfferPropertyCancellationPolicies
